import { useState, FormEvent } from 'react'

import { NextSeo } from 'next-seo'
import { Header } from '@components/Header'
import { Footer } from '@components/Footer'
import { Button } from '@components/UI/Button'
import { Label } from '@components/UI/Form/Label'
import { Input } from '@components/UI/Form/Input'

import { v1 } from '@services/api'
import { TextArea } from '@components/UI/Form/TextArea'

export default function ContactUs() {
  const [error, setError] = useState('')
  const [submitted, setSubmitted] = useState(false)

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()

    async function sendMessage(name: string, email: string, message: string) {
      try {
        const { data } = await v1.post('/users/sendContact', {
          name,
          email,
          message
        })
        if (data.data.statusCode === 200) {
          setSubmitted(true)
        }
      } catch (error) {
        setError('There was an error sending the message')
      }
    }

    const data = new FormData(e.currentTarget)
    const name = data.get('name') as string
    const email = data.get('email') as string
    const message = data.get('message') as string

    if (name && email && message) {
      setError('')
      void sendMessage(name, email, message)
    } else {
      setError('Name, email, and message are required')
    }
  }

  return (
    <>
      <NextSeo
        title="Contact Majordomo | Accurate Home Repair Estimates"
        description="Questions regarding our services or home repair prices? Contact us by filling out the information below and we'll get back to you within 24 hours."
        canonical="https://majordomo.com/contact-home-repair-pricing-company"
        openGraph={{
          url: 'https://majordomo.com/contact-home-repair-pricing-company',
          title: 'Contact Majordomo | Accurate Home Repair Estimates',
          description:
            "Questions regarding our services or home repair prices? Contact us by filling out the information below and we'll get back to you within 24 hours.",
          images: [
            {
              url:
                'https://majordomo.com/images/product-pages/contact-hero.png',
              width: 771,
              height: 690,
              alt: 'Drone shot of roofs of houses'
            }
          ]
        }}
      />
      <Header />

      <main className="pt-8 px-0 container mx-auto">
        <div className="flex md:flex-row flex-col justify-between items-center lg:pb-36 pb-12">
          <img
            src="/images/product-pages/contact-hero.png"
            className="md:order-last order-first md:mb-0 mb-8"
            width="50%"
          />

          {submitted ? (
            <div className="flex flex-col md:items-start items-center justify-center w-full 2xl:p-36 xl:p-20 md:p-8 md:order-first order-last">
              <h1 className="font-black tracking-tight mb-8 text-4xl max-w-lg">
                Thank you so much for contacting Majordomo!{' '}
              </h1>
              <p>We will get back to you as soon as we can!</p>
            </div>
          ) : (
            <div className="flex flex-col md:items-start items-center justify-center w-full 2xl:p-36 xl:p-20 md:p-8 md:order-first order-last">
              <h1 className="font-black tracking-tight mb-8 text-4xl w-full">
                Questions about our home repair pricing reports or have
                feedback?
              </h1>
              <p className="mb-8">
                If you have any questions or comments about Majordomo? We’d love
                to hear from you! Please use our contact portal below to reach
                out and we’ll get back to you as soon as we can.
              </p>
              <form onSubmit={handleSubmit} className="w-full">
                <section className="mb-4">
                  <Label htmlFor="contact-name">Name</Label>
                  <Input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    placeholder="First Last"
                  />
                </section>
                <section className="mb-4">
                  <Label htmlFor="contact-email">Email</Label>
                  <Input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    placeholder="example@example.com"
                  />
                </section>
                <section className="mb-8">
                  <Label htmlFor="contact-message">Message</Label>
                  <TextArea
                    id="contact-message"
                    name="message"
                    required
                    placeholder="Your message here"
                    rows={4}
                  />
                </section>
                {error && (
                  <p className="mb-8 text-red-400 text-center">{error}</p>
                )}
                <section className="flex justify-start">
                  <Button variant="secondary" clipSide="right" wide>
                    Send Message
                  </Button>
                </section>
              </form>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  )
}
